import { queryOptions, useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { api, isTransient, unwrap } from './client';
import type { components } from './schema';

export type Me = components['schemas']['MeResponseDto'];
export type Person = Me['person'];
export type Workspace = Me['workspace'];
export type Membership = Me['memberships'][number];
export type Account = Me['account'];
export type LoginResult = components['schemas']['LoginResponseDto'];

export const authKeys = {
  me: ['auth', 'me'] as const,
  sessions: ['auth', 'sessions'] as const,
};

export const meQueryOptions = queryOptions({
  queryKey: authKeys.me,
  queryFn: () => unwrap(api.GET('/api/v1/auth/me')),
  staleTime: 30_000,
  // Every screen waits on this one, so a deploy restarting the API (a few seconds of 502)
  // or a network blip at start-up must not end on an error page: try again for about
  // 15 s. A 401 is an answer (sign in), never retried.
  retry: (count, error) => isTransient(error) && count < 4,
  retryDelay: (count) => Math.min(8_000, 1_000 * 2 ** count),
});

/** The signed-in person and workspace. Only valid under the `_app` layout, which loads it first. */
export function useMe(): Me {
  return useSuspenseQuery(meQueryOptions).data;
}

export const sessionsQueryOptions = queryOptions({
  queryKey: authKeys.sessions,
  queryFn: () => unwrap(api.GET('/api/v1/auth/sessions')),
});

/**
 * Sign in. One account per email now: the answer carries every organization the
 * account can open, in `memberships`. The session lands on one; more than one
 * means the login screen offers a picker (which calls `useSwitchOrg`).
 */
export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { email: string; password: string }) =>
      unwrap(api.POST('/api/v1/auth/login', { body: { ...body, kind: 'web' } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: authKeys.me }),
  });
}

/**
 * Move the current session into another organization the account belongs to.
 * The caller reloads the page afterwards: everything cached belongs to the old
 * company, so a full reload is the clean way to re-fetch it all for the new one.
 */
export function useSwitchOrg() {
  return useMutation({
    mutationFn: (workspaceId: string) =>
      unwrap(api.POST('/api/v1/auth/switch', { body: { workspaceId } })),
  });
}

/** Start another organization under this account; the session switches into it. */
export function useCreateOrg() {
  return useMutation({
    mutationFn: (body: { name: string; tz?: string }) =>
      unwrap(api.POST('/api/v1/auth/organizations', { body })),
  });
}

export type SignupBody = Omit<components['schemas']['SignupBodyDto'], 'kind' | 'deviceName'>;

/** A new company and its owner. The session cookie comes back with the answer. */
export function useSignup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SignupBody) =>
      unwrap(api.POST('/api/v1/auth/signup', { body: { ...body, kind: 'web' } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: authKeys.me }),
  });
}

export function useChangePassword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { currentPassword: string; newPassword: string }) =>
      unwrap(api.POST('/api/v1/auth/change-password', { body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: authKeys.sessions }),
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => unwrap(api.POST('/api/v1/auth/logout')),
    onSettled: () => {
      qc.clear();
      // A hard navigation drops every in-memory state along with the cookie.
      window.location.assign('/login');
    },
  });
}

export function useRevokeSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(api.DELETE('/api/v1/auth/sessions/{id}', { params: { path: { id } } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: authKeys.sessions }),
  });
}
